// Konva renderer — the only canvas layer. Reconciles store -> stage by id so
// interactions (drag/transform) aren't destroyed mid-gesture. Owns the view
// transform, the selection Transformer, and a draw-preview node.

import Konva from 'konva';
import { coordsToPathData, segmentPathData, lobedPoints, coniferNeedles } from './geometry';
import { pathSegments, pointOnSegment } from '../model/segments';
import { visibleAtMonth } from '../dates';
import { gridSpacing } from '../model/grid';
import { centerOf, contentBounds, strayIds, mapCoords, elementRect, visibleElements, isBoxType } from '../model/bounds';

const DEG = 180 / Math.PI;
const RAD = Math.PI / 180;

function variantKey(el) {
  if (el.type === 'ellipse') return `ellipse:${el.data?.canopyStyle || 'smooth'}`;
  return el.type;
}

// Conifer: filled circle behind radiating needle lines (matches v1).
function coniferSceneFunc(ctx, shape) {
  const r = shape.getAttr('radius') || 0;
  const needles = shape.getAttr('needles') || [];
  const fill = shape.fill();
  if (fill) {
    ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.closePath();
    ctx.fillStyle = fill; ctx.fill();
  }
  ctx.beginPath();
  for (const n of needles) { ctx.moveTo(n.ix, n.iy); ctx.lineTo(n.ox, n.oy); }
  ctx.lineCap = 'round';
  ctx.strokeStyle = shape.stroke() || '#000';
  ctx.lineWidth = shape.getAttr('needleWidth') || 1; // plan units, like v1
  ctx.stroke();
}
function coniferHitFunc(ctx, shape) {
  const r = shape.getAttr('radius') || 0;
  ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.closePath();
  ctx.fillStrokeShape(shape);
}

export default class Renderer {
  constructor(container, store) {
    this.container = container;
    this.store = store;
    this.imageCache = new Map();
    this.nodeMap = new Map(); // element id -> Konva node
    this.draggable = false;   // toggled by the active tool

    this.stage = new Konva.Stage({
      container,
      width: container.clientWidth || 1,
      height: container.clientHeight || 1,
    });

    this.gridLayer = new Konva.Layer({ listening: false });
    this.contentLayer = new Konva.Layer();
    this.stage.add(this.gridLayer, this.contentLayer);

    this.viewGroup = new Konva.Group();
    this.contentLayer.add(this.viewGroup);
    this.labelsGroup = new Konva.Group({ listening: true });
    this.viewGroup.add(this.labelsGroup);
    this.labelMap = new Map(); // element id -> { node, el }
    this.selectionGroup = new Konva.Group({ listening: false }); // orange outlines
    this.viewGroup.add(this.selectionGroup);
    this.hoverGroup = new Konva.Group({ listening: false });
    this.viewGroup.add(this.hoverGroup);
    this.hoverId = null;
    // Poly/line edit handles. Above the Transformer (not inside viewGroup,
    // which sits below it) so a point on the resize box's edge stays
    // grabbable; applyView gives it the same plan transform.
    this.handleView = new Konva.Group({ name: 'handle-view' });
    this.vertexGroup = new Konva.Group({ name: 'vertices' });
    this.handleView.add(this.vertexGroup);

    this.transformer = new Konva.Transformer({
      rotateEnabled: true,
      ignoreStroke: true,
      borderStroke: '#087332',
      anchorStroke: '#087332',
      anchorFill: '#ffffff',
      anchorSize: 8,
      anchorCornerRadius: 4,
      anchorStyleFunc: (anchor) => this.styleSideAnchor(anchor),
    });
    this.contentLayer.add(this.transformer);
    this.contentLayer.add(this.handleView);

    this.previewNode = null;

    this._onResize = () => this.resize();
    window.addEventListener('resize', this._onResize);
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(this._onResize);
      this._ro.observe(container);
    }

    this._unsub = store.subscribe((state, meta) => this.reconcile(state, meta));
  }

  resize() {
    this.stage.size({ width: this.container.clientWidth || 1, height: this.container.clientHeight || 1 });
    this.reconcile(this.store.state, {});
  }

  stageSize() { return { width: this.stage.width(), height: this.stage.height() }; }

  applyView(view) {
    for (const g of [this.viewGroup, this.handleView]) {
      g.position({ x: view.posX || 0, y: view.posY || 0 });
      g.scale({ x: view.scale || 1, y: view.scale || 1 });
    }
  }

  // Pointer position in plan coordinates (inside the scaled/positioned group).
  pointerPlanPos() {
    return this.viewGroup.getRelativePointerPosition();
  }

  // Locked elements (and their labels) never drag; lockLabels pins every label.
  setDraggable(on) {
    this.draggable = on;
    const labelsDraggable = on && this.store.state.settings?.lockLabels !== true;
    for (const [id, node] of this.nodeMap) {
      const el = this.store.state.elements.find((element) => element.id === id);
      node.draggable(on && !el?.data?.locked);
    }
    for (const { node, el } of this.labelMap.values()) node.draggable(labelsDraggable && !el.data?.locked);
  }

  reconcile(state, meta = {}) {
    if (!state.loaded) return;

    let view = state.view;
    if (!view || !view.scale) view = this.fitView(state);
    this.applyView(view);
    this.renderGrid(state, view);

    const layerMap = new Map(state.layers.map((l) => [l.id, l]));
    const seen = new Set();

    for (const el of state.elements) {
      const layer = el.data?.layer ? layerMap.get(el.data.layer) : null;
      const hiddenByLayer = layer && layer.visible === false;
      const hiddenByTime = state.timelineOn && !visibleAtMonth(el, state.timelineMonth);
      const hiddenImage = el.type === 'image' && el.data?.visible === false;
      if (hiddenByLayer || hiddenByTime || hiddenImage) {
        const existing = this.nodeMap.get(el.id);
        if (existing) { existing.destroy(); this.nodeMap.delete(el.id); }
        continue;
      }
      seen.add(el.id);
      let node = this.nodeMap.get(el.id);
      if (!node || node.getAttr('variant') !== variantKey(el)) {
        if (node) node.destroy();
        node = this.buildNode(el);
        this.nodeMap.set(el.id, node);
        this.viewGroup.add(node);
      } else {
        this.applyNode(node, el);
      }
      // Locked elements are inert on the canvas: not draggable, not even hit —
      // clicks fall through to whatever is drawn over a traced-over photo.
      const locked = !!el.data?.locked;
      node.draggable(this.draggable && !locked);
      node.listening(!locked);
      node.opacity(nodeOpacity(el, layer));
    }

    // Remove nodes whose elements are gone
    for (const [id, node] of this.nodeMap) {
      if (!seen.has(id)) { node.destroy(); this.nodeMap.delete(id); }
    }

    // Stack nodes in element-array order (z-order), then overlays on top.
    for (const el of state.elements) this.nodeMap.get(el.id)?.moveToTop();
    this.labelsGroup.moveToTop();
    this.renderSelection(state);
    this.hoverGroup.moveToTop();

    this.renderLabels(state, layerMap);
    this.syncTransformer(state);
    this.renderVertices(state);
    this.contentLayer.batchDraw();
  }

  // Cheap redraw of a few elements whose look changed in place (a live colour,
  // thickness or opacity preview) — no rebuild of every node, label and overlay
  // the way reconcile() does, which is too slow to run on every picker move.
  restyle(ids) {
    const state = this.store.state;
    for (const id of ids) {
      const node = this.nodeMap.get(id);
      const el = node && state.elements.find((e) => e.id === id);
      if (!el) continue;
      this.applyNode(node, el);
      node.opacity(nodeOpacity(el, el.data?.layer ? this.store.layerById(el.data.layer) : null));
    }
    this.contentLayer.batchDraw();
  }

  // Vertex + control-point handles for the single selected polygon/line, so its
  // points and curve/bezier controls can be dragged, plus a midpoint handle on
  // each segment — drag (or click) it to add a point there. Sizes are divided
  // by the view scale to stay ~constant on screen. Handles carry the coord
  // index + slot they edit (anchor / quadratic cp / cubic cp1,cp2 / mid).
  // `keep`: a handle being dragged — it survives the rebuild (destroying it
  // would end the drag), and midpoints hide until it's let go.
  renderVertices(state, { keep = null } = {}) {
    for (const child of this.vertexGroup.getChildren().slice()) if (child !== keep) child.destroy();
    this.vertexGroup.visible(true); // hidden during a transform gesture
    this.vertexGroup.position({ x: 0, y: 0 }); // reset any drag-follow offset
    if (!this.draggable) return;
    const sel = state.selection || [];
    if (sel.length !== 1) return;
    const el = state.elements.find((e) => e.id === sel[0]);
    if (!el || el.data?.locked || (el.type !== 'polygon' && el.type !== 'line')) return;
    const coords = el.data?.coordinates || [];
    if (!coords.some((c) => Array.isArray(c) && typeof c[0] === 'number')) return;
    const scale = state.view?.scale || 1;
    const aR = 5 / scale; const cR = 4 / scale; const mR = 3.9 / scale; const sw = 1.5 / scale;
    const start = coords.find((c) => Array.isArray(c) && typeof c[0] === 'number');
    const closed = el.type === 'polygon';
    const segs = pathSegments(coords, closed);
    // The selected segment (v1): a blue edge under the handles.
    const picked = state.selectedSegment?.id === el.id ? segs.find((sg) => sg.index === state.selectedSegment.index) : null;
    if (picked) {
      this.vertexGroup.add(new Konva.Path({
        data: segmentPathData(picked), stroke: 'rgb(80, 130, 220)', strokeScaleEnabled: false,
        strokeWidth: Math.max(3, Number(el.data?.strokeWidth) > 0 ? el.data.strokeWidth * scale : 1),
        lineCap: 'round', fillEnabled: false, listening: false,
      }));
    }
    // Faint connector from a control point to the anchor(s) it pulls — drawn
    // first so the handles sit on top.
    const link = (ax, ay, bx, by) => this.vertexGroup.add(new Konva.Line({
      points: [ax, ay, bx, by], stroke: '#2f8f57', strokeWidth: 1, strokeScaleEnabled: false,
      dash: [4 / scale, 3 / scale], listening: false, opacity: 0.7,
    }));
    const kept = keep && { vidx: keep.getAttr('vidx'), vkind: keep.getAttr('vkind'), slot: keep.getAttr('slot') };
    const isKept = (vidx, vkind, slot) => kept && kept.vidx === vidx && kept.vkind === vkind && (kept.slot || null) === (slot || null);
    const anchor = (vidx, x, y) => isKept(vidx, 'anchor') || this.vertexGroup.add(new Konva.Circle({
      name: 'vhandle', elId: el.id, vidx, vkind: 'anchor',
      x, y, radius: aR, fill: '#ffffff', stroke: '#087332', strokeWidth: sw, strokeScaleEnabled: false,
      shadowColor: '#0b1f12', shadowBlur: 4 / scale, shadowOpacity: 0.28, shadowOffsetY: 0.5 / scale,
      draggable: true,
    }));
    const cp = (vidx, slot, x, y) => isKept(vidx, 'cp', slot) || this.vertexGroup.add(new Konva.Circle({
      name: 'vhandle', elId: el.id, vidx, vkind: 'cp', slot,
      x, y, radius: cR, fill: '#0CB04D', stroke: '#ffffff', strokeWidth: sw, strokeScaleEnabled: false,
      shadowColor: '#0b1f12', shadowBlur: 3 / scale, shadowOpacity: 0.25,
      draggable: true,
    }));
    // Midpoint: smaller, see-through and mostly white (a hairline of green),
    // so it reads as "add a point here" rather than a point. Too-short segments get none (it would sit on top
    // of the anchors).
    const mid = (seg) => {
      const [x, y] = pointOnSegment(seg, 0.5);
      if (Math.hypot(seg.to[0] - seg.from[0], seg.to[1] - seg.from[1]) * scale < 24 && seg.kind === 'line') return;
      this.vertexGroup.add(new Konva.Circle({
        name: 'vhandle', elId: el.id, vidx: seg.index, vkind: 'mid',
        x, y, radius: mR, fill: 'rgba(255, 255, 255, 0.85)', stroke: '#087332', strokeWidth: sw / 2, strokeScaleEnabled: false,
        opacity: 0.75, hitStrokeWidth: 8 / scale, draggable: true,
      }));
    };
    if (!keep) segs.forEach(mid);
    let prev = start; // previous anchor point, for connector geometry
    coords.forEach((c, i) => {
      if (!Array.isArray(c)) return;
      if (c[0] === 'closing') {
        if (c[1] === 'curve') { link(c[2], c[3], prev[0], prev[1]); link(c[2], c[3], start[0], start[1]); cp(i, 'q', c[2], c[3]); }
        else if (c[1] === 'bezier') { link(c[2], c[3], prev[0], prev[1]); link(c[4], c[5], start[0], start[1]); cp(i, 'c1', c[2], c[3]); cp(i, 'c2', c[4], c[5]); }
        return;
      }
      if (c[2] === 'curve') { link(c[3], c[4], prev[0], prev[1]); link(c[3], c[4], c[0], c[1]); cp(i, 'q', c[3], c[4]); }
      else if (c[2] === 'bezier') { link(c[3], c[4], prev[0], prev[1]); link(c[5], c[6], c[0], c[1]); cp(i, 'c1', c[3], c[4]); cp(i, 'c2', c[5], c[6]); }
      anchor(i, c[0], c[1]);
      prev = c;
    });
    keep?.moveToTop();
    this.handleView.moveToTop();
  }

  // Live-write a dragged handle's position back into the element coordinates and
  // refresh the path (called on every dragmove; commit happens on dragend).
  applyHandle(node) {
    const el = this.store.state.elements.find((e) => e.id === node.getAttr('elId'));
    if (!el || el.data?.locked) return;
    const coords = el.data.coordinates;
    const c = coords[node.getAttr('vidx')];
    if (!Array.isArray(c)) return;
    const x = node.x(); const y = node.y();
    const closing = c[0] === 'closing';
    if (node.getAttr('vkind') === 'anchor') { c[0] = x; c[1] = y; }
    else {
      const slot = node.getAttr('slot');
      if (slot === 'q' || slot === 'c1') { if (closing) { c[2] = x; c[3] = y; } else { c[3] = x; c[4] = y; } }
      else if (slot === 'c2') { if (closing) { c[4] = x; c[5] = y; } else { c[5] = x; c[6] = y; } }
    }
    const shape = this.nodeMap.get(el.id);
    if (shape) { shape.data(coordsToPathData(coords, el.type === 'polygon')); this.contentLayer.batchDraw(); }
  }

  // Species + custom labels as text near each element. Cheap to rebuild.
  renderLabels(state, layerMap) {
    this.labelsGroup.destroyChildren();
    this.labelsGroup.moveToTop();
    this.labelMap.clear();
    if (state.settings.showLabels === false) return;
    // Konva sizes every text node through a shared 2D canvas context. A headless
    // browser whose context has no measureText makes the Konva.Text constructor
    // throw, so skip the labels rather than take the whole render — and the plan
    // load that runs it — down with them.
    if (!canMeasureText()) return;
    for (const el of state.elements) this.addLabel(el, state, layerMap);
  }

  // Labels are sized in screen pixels from the element's on-screen size (v1
  // LabelCollection): hidden below LABEL_CUTOFF px unless hovered, 10px→24px
  // font over 20→1000px, fewer lines on small elements, fading on huge ones.
  addLabel(el, state, layerMap) {
    const d = el.data || {};
    const layer = d.layer ? layerMap.get(d.layer) : null;
    if (layer && layer.visible === false) return;
    if (state.timelineOn && !visibleAtMonth(el, state.timelineMonth)) return;
    if (el.type === 'image' && d.visible === false) return;
    const scale = state.view?.scale || 1;
    const size = elementMaxSize(el) * scale;
    const hovered = el.id === this.hoverId;
    if (size < LABEL_CUTOFF && !hovered) return;
    const eff = Math.max(size, LABEL_CUTOFF);
    const maxLines = Math.floor(1 + (eff - LABEL_CUTOFF) * (4 / (100 - LABEL_CUTOFF)));
    const text = labelText(d.labels, state.species).split('\n').filter(Boolean).slice(0, maxLines).join('\n');
    if (!text) return;
    const c = centerOf(el);
    if (!c) return;
    const fontPx = 10 + (eff - 20) * (14 / 980);
    const t = new Konva.Text({
      text, x: c.x + (d.labelX || 0), y: c.y + (d.labelY || 0),
      fontSize: fontPx / scale, fontFamily: 'sans-serif', fill: '#1f2329', align: 'center',
      opacity: 1 - Math.min(1, Math.max(0, (eff - 500) / 1000)) * (155 / 255),
      name: 'label', elId: el.id, listening: !d.locked,
      // lockLabels prevents accidental label drags (v1 setting)
      draggable: this.draggable && state.settings.lockLabels !== true && !d.locked,
    });
    t.offsetX(t.width() / 2); t.offsetY(t.height() / 2);
    this.labelsGroup.add(t);
    this.labelMap.set(el.id, { node: t, el });
  }

  // Hovering a small element reveals its label (v1): rebuild just the labels
  // of the elements entering/leaving hover. Never mid-drag — that would
  // destroy a label being dragged.
  refreshHoverLabels(ids) {
    const state = this.store.state;
    if (!state.loaded || state.settings.showLabels === false || Konva.isDragging() || !canMeasureText()) return;
    const layerMap = new Map(state.layers.map((l) => [l.id, l]));
    for (const id of ids) {
      if (!id) continue;
      const el = state.elements.find((e) => e.id === id);
      if (!el || elementMaxSize(el) * (state.view?.scale || 1) >= LABEL_CUTOFF) continue;
      this.labelMap.get(id)?.node.destroy();
      this.labelMap.delete(id);
      this.addLabel(el, state, layerMap);
    }
  }

  // Reposition labels to follow their element's live node position (during a
  // drag, before the move is committed). Cheap — only labelled elements.
  repositionLabels() {
    for (const { node, el } of this.labelMap.values()) {
      const shape = this.nodeMap.get(el.id);
      if (!shape) continue;
      const d = el.data || {};
      let cx; let cy;
      if (isBoxType(el.type)) {
        cx = shape.x(); cy = shape.y();
      } else {
        const c = centerOf(el);
        if (!c) continue;
        cx = c.x + shape.x(); cy = c.y + shape.y();
      }
      node.position({ x: cx + (d.labelX || 0), y: cy + (d.labelY || 0) });
    }
    // Hover and selection outlines follow the dragged/transformed shape too —
    // its whole transform, so they grow, shrink and turn with a resize/rotate.
    for (const overlay of [...this.hoverGroup.getChildren(), ...this.selectionGroup.getChildren()]) {
      const n = this.nodeMap.get(overlay.getAttr('elId'));
      if (n) followNode(overlay, n);
    }
    this.contentLayer.batchDraw();
  }

  // Minor/major grid lines in screen space (so they stay crisp), spaced by the
  // current unit dimensions and view scale — scales/pans with the plan, like v1.
  // Lines sit on multiples of the spacing from the plan origin, which is where
  // snap-to-grid puts points (model/grid).
  renderGrid(state, view) {
    this.gridLayer.destroyChildren();
    if (state.settings?.showGrid === false) { this.gridLayer.batchDraw(); return; }
    const w = this.stage.width(); const h = this.stage.height();
    const { minor, major } = gridSpacing(view.scale || 1, state.settings?.units);
    const draw = (spacing, stroke, strokeWidth) => {
      if (!spacing) return;
      const sx = (((view.posX || 0) % spacing) + spacing) % spacing;
      for (let x = sx; x <= w; x += spacing) this.gridLayer.add(new Konva.Line({ points: [Math.round(x) + 0.5, 0, Math.round(x) + 0.5, h], stroke, strokeWidth, listening: false }));
      const sy = (((view.posY || 0) % spacing) + spacing) % spacing;
      for (let y = sy; y <= h; y += spacing) this.gridLayer.add(new Konva.Line({ points: [0, Math.round(y) + 0.5, w, Math.round(y) + 0.5], stroke, strokeWidth, listening: false }));
    };
    if (minor) draw(minor.px, 'rgba(20,28,40,0.04)', 1);
    if (major) draw(major.px, 'rgba(20,28,40,0.088)', 1);
    // origin axes
    this.gridLayer.add(new Konva.Line({ points: [Math.round(view.posX) + 0.5, 0, Math.round(view.posX) + 0.5, h], stroke: 'rgba(20,28,40,0.16)', strokeWidth: 1, listening: false }));
    this.gridLayer.add(new Konva.Line({ points: [0, Math.round(view.posY) + 0.5, w, Math.round(view.posY) + 0.5], stroke: 'rgba(20,28,40,0.16)', strokeWidth: 1, listening: false }));
    this.gridLayer.batchDraw();
  }

  // Yellow hover outline matching the element's exact shape (a plain circle for
  // plants/canopies, the outline for rect/poly/line). Stroke stays ~3px at any
  // zoom (strokeScaleEnabled:false).
  // `el` may also be an array — every element carrying a plant hovered in the
  // Plants tab. Only a single hovered element reveals its (small) label.
  setHover(el) {
    const els = (Array.isArray(el) ? el : [el]).filter(Boolean);
    const id = els.length > 1 ? `many:${els.map((e) => e.id).join(',')}` : (els[0]?.id || null);
    if (id === this.hoverId) return;
    const prevId = this.hoverId;
    this.hoverId = id;
    this.refreshHoverLabels([prevId, this.hoverId]);
    this.hoverGroup.destroyChildren();
    for (const e of els) {
      const node = this.buildHoverNode(e);
      if (!node) continue;
      // Wrapped, so following the shape never clobbers the outline's own x/y.
      const wrap = new Konva.Group({ listening: false });
      wrap.add(node);
      anchorToNode(wrap, e.id, this.nodeMap.get(e.id));
      this.hoverGroup.add(wrap);
    }
    if (els.length) this.hoverGroup.moveToTop();
    this.contentLayer.batchDraw();
  }

  // "Selected" highlight: an orange line traced on each selected element's
  // outline — centred on it, like the hover and picked-segment lines, so the
  // points sit on it — at least 3px (or the element's stroke) on screen at any
  // zoom. Rebuilt every reconcile; drags move it along via repositionLabels().
  renderSelection(state) {
    this.selectionGroup.destroyChildren();
    for (const id of state.selection || []) {
      const node = this.nodeMap.get(id);
      const el = node && state.elements.find((e) => e.id === id);
      const ring = el && this.buildSelectionRing(el, node);
      if (ring) this.selectionGroup.add(ring);
    }
    this.selectionGroup.moveToTop();
  }

  buildSelectionRing(el, node) {
    const d = el.data || {};
    let path;
    if (el.type === 'ellipse' && d.canopyStyle === 'lobed' && node.points) {
      path = closedSplinePath(node.points(), node.tension(), node.x(), node.y()); // follow the lobes
    } else if (el.type === 'ellipse') {
      path = new Path2D(); path.ellipse(d.x, d.y, (d.width || 0) / 2, (d.height || d.width || 0) / 2, 0, 0, Math.PI * 2);
    } else if (el.type === 'rectangle' || el.type === 'image') {
      const hw = (node.width?.() ?? d.width ?? 0) / 2; const hh = (node.height?.() ?? d.height ?? 0) / 2;
      const cos = Math.cos(d.rotation || 0); const sin = Math.sin(d.rotation || 0);
      path = new Path2D();
      [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]].forEach(([px, py], i) => {
        const x = d.x + px * cos - py * sin; const y = d.y + px * sin + py * cos;
        if (i) path.lineTo(x, y); else path.moveTo(x, y);
      });
      path.closePath();
    } else {
      path = new Path2D(coordsToPathData(d.coordinates, el.type === 'polygon'));
    }
    const strokePx = d.strokeColor ? (Number(d.strokeWidth) > 0 ? Math.max(0.5, d.strokeWidth * (this.store.state.view?.scale || 1)) : 1) : 0;
    return new Konva.Shape({
      elId: el.id, baseInv: node.getTransform().copy().invert(), listening: false,
      sceneFunc: (ctx, shape) => {
        const c = ctx._context;
        const scale = shape.getAbsoluteScale().x || 1;
        c.save();
        c.strokeStyle = 'rgb(255, 130, 0)';
        c.lineWidth = Math.max(3, strokePx) / scale;
        c.lineJoin = 'round';
        c.lineCap = 'round';
        c.stroke(path);
        c.restore();
      },
    });
  }

  // Highlight one polygon/line segment (Info tab Dimensions row hover, v1
  // hoverSegment parity). Shares the hover overlay, so setHover(null) clears it.
  setSegmentHover(el, index) {
    const seg = el ? pathSegments(el.data?.coordinates || [], el.type === 'polygon')[index] : null;
    if (!seg) { this.setHover(null); return; }
    if (this.hoverId === `${el.id}#${index}`) return;
    this.hoverId = `${el.id}#${index}`;
    this.hoverGroup.destroyChildren();
    this.hoverGroup.add(new Konva.Path({
      // v1 hoveredSegment: rgb(80,130,220), max(3px, the element's stroke)
      data: segmentPathData(seg), stroke: 'rgb(80, 130, 220)', strokeScaleEnabled: false,
      strokeWidth: Math.max(3, Number(el.data?.strokeWidth) > 0 ? el.data.strokeWidth * (this.store.state.view?.scale || 1) : 1),
      lineCap: 'round', fillEnabled: false, listening: false,
    }));
    this.hoverGroup.moveToTop();
    this.contentLayer.batchDraw();
  }

  buildHoverNode(el) {
    const d = el.data || {};
    const common = { stroke: '#f9bc21', strokeWidth: 3, strokeScaleEnabled: false, fillEnabled: false, listening: false };
    switch (el.type) {
      case 'ellipse': {
        // Lobed canopies: trace the lobes, same spline as the shape itself.
        const lobed = d.canopyStyle === 'lobed' && this.nodeMap.get(el.id);
        if (lobed?.points) return new Konva.Line({ ...common, x: d.x, y: d.y, points: lobed.points(), closed: true, tension: lobed.tension() });
        return new Konva.Ellipse({ ...common, x: d.x, y: d.y, radiusX: (d.width || 0) / 2, radiusY: (d.height || d.width || 0) / 2 });
      }
      case 'rectangle':
        return new Konva.Rect({ ...common, x: d.x, y: d.y, width: d.width || 0, height: d.height || 0, offsetX: (d.width || 0) / 2, offsetY: (d.height || 0) / 2, rotation: (d.rotation || 0) * DEG });
      case 'image': {
        // An unsized image draws at its natural size — outline what's drawn.
        const node = this.nodeMap.get(el.id);
        const w = node?.width() || d.width || 0; const h = node?.height() || d.height || 0;
        return new Konva.Rect({ ...common, x: d.x, y: d.y, width: w, height: h, offsetX: w / 2, offsetY: h / 2, rotation: (d.rotation || 0) * DEG });
      }
      case 'polygon':
        return new Konva.Path({ ...common, data: coordsToPathData(d.coordinates, true) });
      case 'line':
        return new Konva.Path({ ...common, lineCap: 'round', lineJoin: 'round', data: coordsToPathData(d.coordinates, false) });
      default:
        return null;
    }
  }

  buildNode(el) {
    const d = el.data || {};
    let node;
    const base = { id: el.id, elType: el.type, variant: variantKey(el), name: 'element' };
    switch (el.type) {
      case 'ellipse': {
        const r = (d.width || 0) / 2;
        if (d.canopyStyle === 'lobed') {
          node = new Konva.Line({ ...base, x: d.x, y: d.y, points: lobedPoints(d.width || 0, d.canopySeed || 1), closed: true, tension: 0.5, canopy: 'lobed', baseRadius: r });
        } else if (d.canopyStyle === 'conifer') {
          const cn = coniferNeedles(d.width || 0, d.canopySeed || 1);
          node = new Konva.Shape({ ...base, x: d.x, y: d.y, sceneFunc: coniferSceneFunc, hitFunc: coniferHitFunc, radius: cn.radius, needles: cn.needles, canopy: 'conifer', baseRadius: r });
          // A custom Shape has no size, so its bounds (and the transformer box)
          // would collapse to a point — report the canopy circle instead.
          node.getSelfRect = coniferSelfRect;
        } else {
          node = new Konva.Ellipse({ ...base, x: d.x, y: d.y, radiusX: r, radiusY: (d.height || d.width || 0) / 2 });
        }
        break;
      }
      case 'rectangle':
        node = new Konva.Rect({ ...base, x: d.x, y: d.y, width: d.width || 0, height: d.height || 0, offsetX: (d.width || 0) / 2, offsetY: (d.height || 0) / 2, rotation: (d.rotation || 0) * DEG });
        break;
      case 'polygon':
        node = new Konva.Path({ ...base, data: coordsToPathData(d.coordinates, true) });
        node.hitStrokeWidth(Math.max(10, d.strokeWidth || 0));
        break;
      case 'line':
        node = new Konva.Path({ ...base, data: coordsToPathData(d.coordinates, false), lineCap: 'round', lineJoin: 'round' });
        break; // hit width is zoom-dependent — set in styleNode
      case 'image': {
        const image = this.loadImage(d.url);
        const width = d.width || image?.width || 0;
        const height = d.height || image?.height || 0;
        node = new Konva.Image({ ...base, image, url: d.url, x: d.x || 0, y: d.y || 0, width, height, offsetX: width / 2, offsetY: height / 2, rotation: (d.rotation || 0) * DEG });
        break;
      }
      default:
        return new Konva.Group(base);
    }
    this.styleNode(node, el);
    return node;
  }

  applyNode(node, el) {
    const d = el.data || {};
    if (el.type === 'ellipse') {
      const r = (d.width || 0) / 2;
      if (d.canopyStyle === 'lobed') {
        node.points(lobedPoints(d.width || 0, d.canopySeed || 1)); node.position({ x: d.x, y: d.y }); node.setAttr('baseRadius', r);
      } else if (d.canopyStyle === 'conifer') {
        const cn = coniferNeedles(d.width || 0, d.canopySeed || 1);
        node.setAttr('radius', cn.radius); node.setAttr('needles', cn.needles); node.position({ x: d.x, y: d.y }); node.setAttr('baseRadius', r);
      } else {
        node.position({ x: d.x, y: d.y }); node.radiusX(r); node.radiusY((d.height || d.width || 0) / 2);
      }
    } else if (el.type === 'rectangle' || el.type === 'image') {
      const image = el.type === 'image' ? this.loadImage(d.url) : null;
      const width = d.width || image?.width || 0;
      const height = d.height || image?.height || 0;
      node.position({ x: d.x, y: d.y }); node.width(width); node.height(height);
      node.offsetX(width / 2); node.offsetY(height / 2); node.rotation((d.rotation || 0) * DEG);
      if (el.type === 'image') { node.image(image); node.setAttr('url', d.url); }
    } else {
      node.data(coordsToPathData(d.coordinates, el.type === 'polygon')); node.position({ x: 0, y: 0 });
    }
    this.styleNode(node, el);
  }

  // Strokes follow v1 Appearance.scaledStrokeWidth: a set width scales with
  // the zoom but never drops below 0.5px on screen; no width = a constant 1px
  // hairline. (A 1-plan-unit default vanished when zoomed out.)
  styleNode(node, el) {
    if (el.type === 'image') return; // pixels, no fill/stroke
    const d = el.data || {};
    const scale = this.store.state.view?.scale || 1;
    node.fill(el.type === 'line' ? undefined : (d.fillColor || undefined));
    node.stroke(d.strokeColor || undefined);
    node.setAttr('needleWidth', d.strokeWidth ?? 1);
    if (!d.strokeColor) { node.strokeWidth(0); return; }
    const w = Number(d.strokeWidth);
    const hairline = d.strokeWidth == null || d.strokeWidth === '' || !(w > 0);
    node.strokeScaleEnabled(!hairline);
    node.strokeWidth(hairline ? 1 : Math.max(0.5 / scale, w));
    // Lines are hit (and hovered) within LINE_HIT_PX screen pixels of the path,
    // or half their drawn width if that's wider — v1's tolerance. The hit
    // stroke shares the stroke's units: screen px for hairlines, plan units
    // (scaled by the zoom) otherwise.
    if (el.type === 'line') node.hitStrokeWidth(hairline ? LINE_HIT_PX * 2 : Math.max((LINE_HIT_PX * 2) / scale, w));
  }

  // Polygons/lines stretch in one direction by dragging a whole side of the
  // resize box (v1) — not a mid-edge handle, which would sit on top of the
  // segment midpoints. The side anchors become invisible bands along the
  // edges, between the corner anchors; Konva gives them their resize cursor.
  styleSideAnchor(anchor) {
    if (!this._sideStretch) return;
    const side = ['top-center', 'bottom-center', 'middle-left', 'middle-right'].find((n) => anchor.hasName(n));
    if (!side) return;
    const back = anchor.getParent()?.findOne('.back');
    if (!back) return;
    const band = 10;
    const corner = this.transformer.anchorSize() + 4;
    const horizontal = side.endsWith('center');
    const length = (horizontal ? Math.abs(back.width()) : Math.abs(back.height())) - corner * 2;
    // A flat box (a horizontal or vertical line) has nothing to stretch that way.
    if (length < 8) { anchor.visible(false); return; }
    anchor.setAttrs({
      width: horizontal ? length : band,
      height: horizontal ? band : length,
      offsetX: horizontal ? length / 2 : band / 2,
      offsetY: horizontal ? band / 2 : length / 2,
      fill: 'rgba(0, 0, 0, 0.001)', stroke: null, strokeWidth: 0, cornerRadius: 0,
    });
  }

  syncTransformer(state) {
    const nodes = (state.selection || [])
      .filter((id) => !state.elements.find((el) => el.id === id)?.data?.locked)
      .map((id) => this.nodeMap.get(id)).filter(Boolean);
    const types = new Set(nodes.map((n) => n.getAttr('elType')));
    // Corner anchors only unless a single rectangle or path is selected:
    //  - ellipses are circles on the wire (v1 reads only `width`), so the ratio
    //    has to hold;
    //  - images resize proportionally (a stretched photo misleads the plan);
    //  - polygons/lines stretch by a whole side instead (styleSideAnchor);
    //  - a multi-selection scales uniformly: stretching the shared box skews
    //    rotated rectangles into parallelograms, which the format can't store.
    const multi = nodes.length > 1;
    const rectsOnly = !multi && types.has('rectangle');
    const pathsOnly = !multi && [...types].some((t) => t === 'polygon' || t === 'line');
    this._sideStretch = pathsOnly;
    this.transformer.enabledAnchors(rectsOnly || pathsOnly
      ? ['top-left', 'top-center', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-center', 'bottom-right']
      : ['top-left', 'top-right', 'bottom-left', 'bottom-right']);
    // Circles, images and multi-selections keep their proportions: a circle
    // can't become an ellipse on the wire, a stretched photo misleads the
    // plan, and a stretched group skews its rotated members.
    // Shift would toggle that off mid-gesture, so it's disabled whenever the
    // ratio is enforced — unlocking an image is a checkbox in Properties.
    const lockedRatio = multi || types.has('ellipse') || nodes.some((n) => n.getAttr('elType') === 'image'
      && state.elements.find((el) => el.id === n.id())?.data?.lockRatio !== false);
    this.transformer.keepRatio(lockedRatio);
    this.transformer.shiftBehavior(lockedRatio ? 'none' : 'default');
    // Everything rotates but a lone circle (rotation-invariant). A group turns
    // around its centre: circles only move, rectangles and images keep their
    // rotation, and polygons and lines bake it into their points (bakeNode),
    // so the format needs nowhere to store it.
    this.transformer.rotateEnabled(nodes.length !== 1 || !types.has('ellipse'));
    this.transformer.nodes(this.draggable ? nodes : []);
    this.transformer.moveToTop();
  }

  // Read a node's current geometry back into element data (after drag/transform).
  bakeNode(node, el) {
    if (el.data?.locked) return;
    const d = el.data;
    if (el.type === 'ellipse' && node.getAttr('canopy')) {
      const scale = (Math.abs(node.scaleX()) + Math.abs(node.scaleY())) / 2 || 1;
      const r = Math.max(2, node.getAttr('baseRadius') * scale);
      node.scale({ x: 1, y: 1 }); node.rotation(0); // a group rotation only moves it
      d.x = node.x(); d.y = node.y(); d.width = r * 2; d.height = r * 2;
    } else if (el.type === 'ellipse') {
      // Circles on the wire: v1 reads only `width`, so bake any anisotropy
      // (legacy oval data, group transforms) down to the average radius.
      const r = Math.max(2, (node.radiusX() * Math.abs(node.scaleX()) + node.radiusY() * Math.abs(node.scaleY())) / 2);
      node.scale({ x: 1, y: 1 }); node.rotation(0); node.radiusX(r); node.radiusY(r);
      d.x = node.x(); d.y = node.y(); d.width = r * 2; d.height = r * 2;
    } else if (el.type === 'rectangle' || el.type === 'image') {
      const w = Math.max(4, node.width() * node.scaleX());
      const h = Math.max(4, node.height() * node.scaleY());
      node.scale({ x: 1, y: 1 }); node.width(w); node.height(h); node.offsetX(w / 2); node.offsetY(h / 2);
      d.x = node.x(); d.y = node.y(); d.width = w; d.height = h; d.rotation = node.rotation() * RAD;
    } else {
      // poly/line: push the node's whole transform (drag offset AND any resize
      // scale) through every coordinate — control points included — then reset
      // the node so its path data is authoritative again.
      const t = node.getTransform().copy();
      d.coordinates = mapCoords(d.coordinates, (x, y) => {
        const p = t.point({ x, y });
        return [p.x, p.y];
      });
      node.setAttrs({ x: 0, y: 0, scaleX: 1, scaleY: 1, rotation: 0 });
      node.data(coordsToPathData(d.coordinates, el.type === 'polygon'));
    }
  }

  // Read an image once and reconcile when it becomes available. URLs stay in
  // runtime state only — the content serializer removes them before saving.
  // The loaded picture's own size, or null while it's still loading.
  naturalImageSize(url) {
    const image = url && this.imageCache.get(url);
    return image?.naturalWidth ? { width: image.naturalWidth, height: image.naturalHeight } : null;
  }

  loadImage(url) {
    if (!url) return null;
    const cached = this.imageCache.get(url);
    if (cached) return cached;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    // Through the store, so the panels (natural width/height) refresh too.
    image.onload = () => this.store.emit({ source: 'image' });
    image.src = url;
    this.imageCache.set(url, image);
    return image;
  }

  // During an element drag, slide the selected poly/line's vertex handles so they
  // track the shape (the handles hold absolute coords; the node holds the offset).
  offsetVertices(node) {
    const t = node?.getAttr?.('elType');
    if (t === 'polygon' || t === 'line') {
      this.vertexGroup.position({ x: node.x(), y: node.y() });
      this.contentLayer.batchDraw();
    }
  }

  // Abort any in-progress Konva drag (e.g. when a second finger starts a pinch).
  stopAllDrag() {
    for (const n of this.nodeMap.values()) if (n.isDragging?.()) n.stopDrag();
    for (const { node } of this.labelMap.values()) if (node.isDragging?.()) node.stopDrag();
  }

  // PNG blob of the whole plan (no grid / handles / hover) for the overview
  // thumbnail, framed on the content rather than whatever the user happened to
  // be zoomed into. Konva renders toBlob's canvas synchronously and only the
  // PNG encoding is async, so the live stage is rearranged and restored within
  // one tick and never drawn in its thumbnail state (drawing it here flashed a
  // zoomed-out frame after every autosave). Returns null if the canvas is
  // tainted (cross-origin image).
  snapshotBlob() {
    const gridVis = this.gridLayer.visible();
    const hoverVis = this.hoverGroup.visible();
    const selVis = this.selectionGroup.visible();
    const vertexVis = this.vertexGroup.visible();
    const tfNodes = this.transformer.nodes();
    this.gridLayer.visible(false); this.hoverGroup.visible(false); this.vertexGroup.visible(false); this.selectionGroup.visible(false);
    this.transformer.nodes([]);
    this.applyView(this.fitView(this.store.state));
    let blob;
    try {
      const max = Math.max(this.stage.width(), this.stage.height()) || 1;
      const pixelRatio = Math.min(1, 900 / max);
      blob = this.stage.toBlob({ pixelRatio, mimeType: 'image/png' });
    } catch (_) {
      blob = Promise.resolve(null);
    } finally {
      this.gridLayer.visible(gridVis); this.hoverGroup.visible(hoverVis); this.vertexGroup.visible(vertexVis); this.selectionGroup.visible(selVis);
      this.transformer.nodes(tfNodes);
      this.applyView(this.store.state.view);
    }
    return blob.catch(() => null);
  }

  // --- preview node for draw tools -----------------------------------------
  // Never hit-tested: the shape being drawn is only a picture of the gesture.
  setPreview(node) { this.clearPreview(); node.listening(false); this.previewNode = node; this.viewGroup.add(node); this.contentLayer.batchDraw(); }
  clearPreview() { if (this.previewNode) { this.previewNode.destroy(); this.previewNode = null; this.contentLayer.batchDraw(); } }

  // Fit framing the main cluster — a stray element far from the rest no longer
  // blows up the view (it's excluded), and the scale is clamped to a sane range.
  // Only drawn elements count: hidden layers/images can't pull the view.
  fitView(state) {
    return this.boundsView(contentBounds(this.framedElements(state)), 0.02, 4);
  }

  // Fit framing EVERYTHING (including strays) — used by "Show" on the warning.
  fitAllView(state) {
    return this.boundsView(contentBounds(visibleElements(state)), 0.005, 4);
  }

  // The visible elements minus strays (all visible ones if every one is a stray).
  framedElements(state) {
    const visible = visibleElements(state);
    const strays = new Set(strayIds(visible));
    const elements = visible.filter((el) => !strays.has(el.id));
    return elements.length ? elements : visible;
  }

  // Recentre at 100% zoom, without changing how big anything looks. Centring on
  // the bounds midpoint alone can land in a gap on a spread-out plan, so aim at
  // the element nearest that midpoint — the jump then always hits something.
  centerView(state) {
    const w = this.stage.width(); const h = this.stage.height();
    const pool = this.framedElements(state);
    const b = contentBounds(pool);
    if (!b) return { scale: 1, posX: w / 2, posY: h / 2 };
    let target = { x: b.cx, y: b.cy }; let best = Infinity;
    for (const el of pool) {
      const r = elementRect(el);
      if (!r) continue;
      const cx = (r.minX + r.maxX) / 2; const cy = (r.minY + r.maxY) / 2;
      const d = Math.hypot(cx - b.cx, cy - b.cy);
      if (d < best) { best = d; target = { x: cx, y: cy }; }
    }
    return { scale: 1, posX: w / 2 - target.x, posY: h / 2 - target.y };
  }

  boundsView(b, minScale, maxScale) {
    const w = this.stage.width(); const h = this.stage.height();
    if (!b) return { scale: 1, posX: w / 2, posY: h / 2 };
    let scale = Math.min(w / (b.w || 1), h / (b.h || 1)) * 0.9 || 1;
    scale = Math.max(minScale, Math.min(maxScale, scale));
    return { scale, posX: w / 2 - b.cx * scale, posY: h / 2 - b.cy * scale };
  }

  fitToContent() { this.store.set({ view: this.fitView(this.store.state) }, { source: 'fit' }); }
  fitAll() { this.store.set({ view: this.fitAllView(this.store.state) }, { source: 'fit' }); }
  centerOnContent() { this.store.set({ view: this.centerView(this.store.state) }, { source: 'fit' }); }

  // Visible elements whose center sits far outside the cluster (mis-placed strays).
  strayElementIds(state) { return strayIds(visibleElements(state)); }

  destroy() {
    this._unsub?.();
    window.removeEventListener('resize', this._onResize);
    this._ro?.disconnect();
    this.stage.destroy();
  }
}

// --- helpers ---------------------------------------------------------------
// Closed cardinal spline through flat [x,y,…] points as a Path2D — the same
// control-point maths as Konva.Line({ closed, tension }), so an outline built
// from it sits exactly on a lobed canopy.
function closedSplinePath(pts, tension, ox = 0, oy = 0) {
  const n = pts.length / 2;
  const path = new Path2D();
  if (n < 3) return path;
  const P = (i) => { const k = ((i % n) + n) % n; return [pts[2 * k] + ox, pts[2 * k + 1] + oy]; };
  // Konva getControlPoints: [in-control, out-control] around point i.
  const cps = (i) => {
    const [x0, y0] = P(i - 1); const [x1, y1] = P(i); const [x2, y2] = P(i + 1);
    const d01 = Math.hypot(x1 - x0, y1 - y0); const d12 = Math.hypot(x2 - x1, y2 - y1);
    const fa = (tension * d01) / (d01 + d12 || 1); const fb = (tension * d12) / (d01 + d12 || 1);
    return [[x1 - fa * (x2 - x0), y1 - fa * (y2 - y0)], [x1 + fb * (x2 - x0), y1 + fb * (y2 - y0)]];
  };
  const first = P(0);
  path.moveTo(first[0], first[1]);
  for (let i = 0; i < n; i += 1) {
    const out = cps(i)[1]; const inn = cps(i + 1)[0]; const [x, y] = P(i + 1);
    path.bezierCurveTo(out[0], out[1], inn[0], inn[1], x, y);
  }
  path.closePath();
  return path;
}

const LABEL_CUTOFF = 50; // px on screen, v1 LabelCollection.cutOff
const LINE_HIT_PX = 7; // px on screen either side of a line, v1 Line tolerance

// Largest plan-unit extent of an element (v1 currentMaxSize / scale).
function elementMaxSize(el) {
  const d = el.data || {};
  if (el.type === 'ellipse') return d.width || 0;
  if (el.type === 'rectangle' || el.type === 'image') return Math.max(d.width || 0, d.height || 0);
  const r = elementRect(el);
  return r ? Math.max(r.maxX - r.minX, r.maxY - r.minY) + (Number(d.strokeWidth) || 0) : 0;
}

// A layer's opacity, times an image's own.
function nodeOpacity(el, layer) {
  return (layer ? (layer.opacity ?? 1) : 1) * (el.type === 'image' ? (el.data.opacity ?? 255) / 255 : 1);
}

function coniferSelfRect() {
  const r = this.getAttr('radius') || 0;
  return { x: -r, y: -r, width: r * 2, height: r * 2 };
}

// Hover/selection outlines are drawn from the element's committed geometry.
// Remember the shape's transform at that moment, so a live drag/transform can
// be replayed onto the outline as the delta from there.
function anchorToNode(overlay, elId, node) {
  overlay.setAttr('elId', elId);
  if (node) overlay.setAttr('baseInv', node.getTransform().copy().invert());
}

function followNode(overlay, node) {
  const baseInv = overlay.getAttr('baseInv');
  if (!baseInv) return;
  overlay.setAttrs(node.getTransform().copy().multiply(baseInv).decompose());
}

// Whether the browser's 2D canvas context can measure text. Probed once: the
// result is a property of the environment, not of any one render.
let _canMeasureText;
function canMeasureText() {
  if (_canMeasureText === undefined) {
    try {
      const ctx = document.createElement('canvas').getContext('2d');
      _canMeasureText = !!ctx && typeof ctx.measureText === 'function';
    } catch (_) {
      _canMeasureText = false;
    }
  }
  return _canMeasureText;
}

function labelText(labels = [], species = {}) {
  const lines = [];
  for (const label of labels || []) {
    if (label.type === 'custom') {
      if (label.visible !== false && label.value) lines.push(label.value);
    } else if (label.type === 'species') {
      const s = species[label.id];
      if (!s) continue;
      for (const key of label.keys || []) {
        const v = Array.isArray(s[key]) ? s[key][0] : s[key];
        if (v) lines.push(v);
      }
    }
  }
  return lines.join('\n');
}

